import React, { useEffect, useRef } from 'react';
import * as S from './styles';

const stats = [
  { value: 500, suffix: '+', label: 'Organizadores Ativos' },
  { value: 12000, suffix: 'k', label: 'Passageiros Gerenciados' },
  { value: 98, suffix: '%', label: 'Satisfação dos Usuários' },
];

const useCountUp = (
  target: number,
  suffix: string,
  isVisible: boolean
): string => {
  const [displayValue, setDisplayValue] = React.useState(
    suffix === 'k' ? '+0k' : suffix === '%' ? '0%' : '+0'
  );
  const animatedRef = useRef(false);

  useEffect(() => {
    if (!isVisible || animatedRef.current) return;

    animatedRef.current = true;
    const duration = 1500;
    const startTime = Date.now();

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const val = eased * target;

      let text: string;
      if (suffix === 'k') {
        text = '+' + (val / 1000).toFixed(val / 1000 < 10 ? 1 : 0) + 'k';
      } else if (suffix === '%') {
        text = Math.floor(val) + '%';
      } else {
        text = '+' + Math.floor(val);
      }

      setDisplayValue(text);
      if (progress < 1) requestAnimationFrame(animate);
    };

    requestAnimationFrame(animate);
  }, [target, suffix, isVisible]);

  return displayValue;
};

export const StatsBar: React.FC = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = React.useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.5 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <S.Container id="stats-bar" ref={ref}>
      {stats.map((stat) => (
        <S.StatItem key={stat.label}>
          <StatNumDisplay
            target={stat.value}
            suffix={stat.suffix}
            isVisible={isVisible}
          />
          <S.StatDesc>{stat.label}</S.StatDesc>
        </S.StatItem>
      ))}
    </S.Container>
  );
};

const StatNumDisplay: React.FC<{
  target: number;
  suffix: string;
  isVisible: boolean;
}> = ({ target, suffix, isVisible }) => {
  const displayValue = useCountUp(target, suffix, isVisible);
  return <S.StatNum>{displayValue}</S.StatNum>;
};
